import { Button, Link } from '@appsumo/dorado-react';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Shelf } from '~/components/common/Shelf';
import { currency } from '~/lib/format';
import useUser from '~/lib/user';
import { LOGIN_URL } from '~/lib/util/constants';
import plusCheck from '~/public/plus-ready-red-check.png';
import closeIcon from '~/public/toaster/close.png';

export interface PlusShelfProps {
  open: boolean;
  onClose: () => void;
  continueWithPlus: () => void;
  dealSlug?: string;
  portalSelector?: string;
}

interface PlusShelfPerk {
  description: string;
  cta?: string;
}

const plusShelfPerksArray: Array<PlusShelfPerk> = [
  {
    description: '10% off every tool you buy',
  },
  {
    description: 'FREE access to AppSumo Originals',
  },
  {
    description: 'Access to our business community The Sauce',
  },
  {
    description:
      'Extra time to buy or upgrade on top deals you may have missed...',
    cta: 'and much much more!',
  },
];

function plusShelfPerks(plusDealRef?: string) {
  const plusLink = plusDealRef ? `/plus?ref=${plusDealRef}` : '/plus';
  return (
    <ul className="mt-2">
      {plusShelfPerksArray.map((perk, index) => (
        <li key={index} className="flex">
          <Image
            src={plusCheck}
            alt="Check"
            height={90}
            width={80}
            className="h-[18px] w-4"
          />
          <span className="ml-2">
            {perk.description}
            {perk.cta && (
              <Link href={plusLink} target="_blank" styled>
                {perk.cta}
              </Link>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

function PlusShelfContent({
  continueWithPlus,
  onClose,
  dealSlug,
  showLogin = false,
}: {
  continueWithPlus: () => void;
  onClose: () => void;
  dealSlug?: string;
  showLogin?: boolean;
}) {
  const plusDealRef = dealSlug ? `plus-page-${dealSlug}` : undefined;

  return (
    <>
      <div className="h-screen bg-gradient-to-b from-[#FFE5E0] via-white p-6">
        <div
          className="flex items-start justify-end gap-8 self-stretch"
          data-testid="plus-shelf-close-button"
        >
          <Image
            src={closeIcon}
            alt="Close"
            height={24}
            width={24}
            className="cursor-pointer"
            onClick={onClose}
          />
        </div>
        <div
          className="mb-6 mt-2 text-center text-2xl font-extrabold"
          data-testid="plus-shelf-title"
        >
          AppSumo Plus members save&nbsp;
          <span className="text-ready-red">$350+/year</span>
          &nbsp;on essential tools. Join Plus and start saving today!
        </div>
        <div className="mb-6">
          <p className="font-semibold">It’s all about the perks</p>
          {plusShelfPerks(plusDealRef)}
        </div>
        {/* Plus cost */}
        <div className="text-center">
          <span className="text-2xl font-medium" data-testid="plus-shelf-price">
            {currency(99)}
          </span>
          /annual
          <span
            className="ml-2 font-normal text-grace line-through"
            data-testid="plus-shelf-strikethrough-price"
          >
            {currency(3370)}
          </span>
        </div>
        {/* Join plus button */}
        <Button className="mt-4 w-full" onClick={continueWithPlus}>
          Continue with Plus membership
        </Button>
      </div>
      {showLogin && (
        <div
          className="border-t border-t-gravel py-4 px-2 text-center font-sans"
          data-testid="plus-shelf-login"
        >
          <span className="appsumo-style-links">
            Already have a Plus membership?&nbsp;
            <a href={`${LOGIN_URL}?next=/products/${dealSlug}/`}>Log in here</a>
          </span>
        </div>
      )}
    </>
  );
}

export function PlusShelf({
  open,
  onClose,
  continueWithPlus,
  dealSlug,
  portalSelector,
}: PlusShelfProps) {
  const ref = useRef<Element>();
  const [mounted, setMounted] = useState(false);
  const { user } = useUser();

  const selector = portalSelector || 'headlessui-portal-root';

  useEffect(() => {
    ref.current = document.getElementById(selector)!;
    setMounted(true);
  }, [selector]);

  return mounted
    ? createPortal(
        <Shelf open={open} onClose={onClose}>
          <PlusShelfContent
            continueWithPlus={() => {
              continueWithPlus();
              onClose();
            }}
            onClose={onClose}
            dealSlug={dealSlug}
            showLogin={!user}
          />
        </Shelf>,
        ref.current!,
      )
    : null;
}
